/**
 * The table answering the phone being moved.
 *
 * All this does is keep two numbers — `--tilt-x` and `--tilt-y`, each in
 * [−1, 1] — on the screens that hold it (the felt, the hand band), and set
 * `data-tilt` on <html> while it is running. The numbers go on those screens,
 * not on <html>: a custom property changed on the root is inherited by every
 * element in the page, so each frame restyled the whole app to move a few
 * cards. Everything that moves
 * because of them is CSS (app.css, "tilt"): the trick drifts a few pixels over
 * the felt, its shadows swing the other way, the lamp's sheen slides across the
 * wool and a glint runs along the cards. None of it is a control and none of it
 * changes where anything *is* for a tap, so it can never cost a misplay.
 *
 * ## Movement, not attitude
 *
 * Nobody holds a phone level. The reading is taken against a baseline that
 * follows the phone's attitude slowly (a few seconds), so however you are
 * holding it the table settles flat, and it is *moving* the phone — shifting
 * in your seat, tipping it towards someone to show them — that the cards
 * answer. Held at an angle for long, they drift back to rest.
 *
 * ## Where there is no gyroscope
 *
 * A desktop with a fine pointer gets the same effect from the pointer's place
 * over the window, which is also what makes it testable without a phone.
 */
import { getSettings, prefersReducedMotion, subscribeSettings, type Settings } from '../settings/settings'
import { probe } from '../debug/probe'

/** Degrees of movement away from the baseline that reads as full tilt. */
const FULL_DEG = 14
/** How quickly the baseline catches up with the way the phone is held. */
const BASELINE_MS = 2600
/** Per-step (30 Hz) easing towards the latest reading, so a noisy sensor is not a
 *  jittery table. */
const FOLLOW = 0.26
/** A reading has to move this far (of full tilt, so about 0.7°) from the one
 *  being followed before it is followed at all. A hand is never still: its
 *  tremor alone used to keep the table redrawing sixty times a second for as
 *  long as the phone was held, which is a warm phone for a glint nobody can
 *  see move. */
const DEAD_ZONE = 0.05
/** At most this often, ms. The drift is slow and eased; a 30 Hz table looks
 *  the same as a 60 Hz one and costs half. */
const FRAME_MS = 32
/** Changes smaller than this are not written: every write restyles the table,
 *  and a hundredth of full tilt is under a pixel everywhere it is used. */
const EPSILON = 0.01

type OrientationWithPermission = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<'granted' | 'denied'>
}

let running = false
let target = { x: 0, y: 0 }
let shown = { x: 0, y: 0 }
let written = { x: NaN, y: NaN }
let baseline: { x: number; y: number; at: number } | null = null
let frame = 0
let lastStep = 0

/** iOS asks before a page may read the gyroscope, and only from a tap. True
 *  when the answer is yes or when there is nobody to ask. */
export async function requestTiltPermission(): Promise<boolean> {
  const DOE = typeof DeviceOrientationEvent === 'undefined' ? undefined : (DeviceOrientationEvent as OrientationWithPermission)
  if (!DOE?.requestPermission) {
    probe('tilt permission', 'not needed')
    return true
  }
  try {
    const answer = await DOE.requestPermission()
    probe('tilt permission', answer)
    return answer === 'granted'
  } catch (e) {
    probe('tilt permission', `threw: ${String(e)}`)
    return false
  }
}

/** Whether a permission has to be asked for before tilt can work. */
export function tiltNeedsPermission(): boolean {
  return typeof DeviceOrientationEvent !== 'undefined' && !!(DeviceOrientationEvent as OrientationWithPermission).requestPermission
}

/** Start following the settings. Called once, at boot. */
export function initTilt(): void {
  sync(getSettings())
  subscribeSettings(sync)
  if (typeof matchMedia === 'function') {
    matchMedia('(prefers-reduced-motion: reduce)').addEventListener?.('change', () => sync(getSettings()))
  }
  document.addEventListener('visibilitychange', () => sync(getSettings()))
}

/** How many things on screen want the table to answer the phone. The sensor is
 *  read only while one does — the lobby, the history and the scorepad have no
 *  cards on them to catch the light, and a sensor listened to for nothing is
 *  battery spent on nothing. */
let holders = 0

/** Where the numbers are written: the holders' own elements. */
const targets = new Set<HTMLElement>()

/** Held by a screen with cards on it for as long as it is up (Felt, HandBand),
 *  which is also where the numbers are written. Returns the release. */
export function holdTilt(el: HTMLElement | null): () => void {
  holders++
  if (el) {
    targets.add(el)
    if (running && !Number.isNaN(written.x)) write(el, written)
  }
  sync(getSettings())
  let held = true
  return () => {
    if (!held) return
    held = false
    holders--
    if (el) {
      targets.delete(el)
      clear(el)
    }
    sync(getSettings())
  }
}

function write(el: HTMLElement, at: { x: number; y: number }): void {
  el.style.setProperty('--tilt-x', at.x.toFixed(3))
  el.style.setProperty('--tilt-y', at.y.toFixed(3))
}

function clear(el: HTMLElement): void {
  el.style.removeProperty('--tilt-x')
  el.style.removeProperty('--tilt-y')
}

function sync(s: Settings): void {
  const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden'
  if (s.tilt && holders > 0 && visible && !prefersReducedMotion()) start()
  else stop()
}

function start(): void {
  if (running) return
  probe('tilt', 'running')
  running = true
  baseline = null
  window.addEventListener('deviceorientation', onOrientation)
  if (typeof matchMedia === 'function' && matchMedia('(hover: hover) and (pointer: fine)').matches) {
    window.addEventListener('pointermove', onPointer, { passive: true })
  }
  // iOS forgets the grant with the page. Turning tilt on from Settings asks at
  // once; after a reload the first tap anywhere asks again, since a tap is
  // the only thing allowed to.
  if (tiltNeedsPermission()) window.addEventListener('pointerup', askOnce, { once: true })
  document.documentElement.dataset.tilt = ''
}

function stop(): void {
  probe('tilt', 'stopped')
  if (!running) return
  running = false
  window.removeEventListener('deviceorientation', onOrientation)
  window.removeEventListener('pointermove', onPointer)
  window.removeEventListener('pointerup', askOnce)
  if (frame) cancelAnimationFrame(frame)
  frame = 0
  target = { x: 0, y: 0 }
  shown = { x: 0, y: 0 }
  written = { x: NaN, y: NaN }
  delete document.documentElement.dataset.tilt
  for (const el of targets) clear(el)
}

function askOnce(): void {
  void requestTiltPermission()
}

function onOrientation(e: DeviceOrientationEvent): void {
  if (e.beta === null || e.gamma === null) return
  const [x, y] = toScreen(e.gamma, e.beta)
  const now = performance.now()
  if (!baseline) {
    baseline = { x, y, at: now }
  } else {
    const k = 1 - Math.exp(-(now - baseline.at) / BASELINE_MS)
    baseline = { x: baseline.x + (x - baseline.x) * k, y: baseline.y + (y - baseline.y) * k, at: now }
  }
  follow(clamp((x - baseline.x) / FULL_DEG), clamp((y - baseline.y) / FULL_DEG))
}

/** The sensor reports against the device, and the table is drawn against the
 *  screen, so a phone turned on its side has to swap the axes back. */
function toScreen(gamma: number, beta: number): [number, number] {
  const angle = typeof screen !== 'undefined' && screen.orientation ? screen.orientation.angle : 0
  switch (angle) {
    case 90:
      return [beta, -gamma]
    case 180:
      return [-gamma, -beta]
    case 270:
      return [-beta, gamma]
    default:
      return [gamma, beta]
  }
}

function onPointer(e: PointerEvent): void {
  if (e.pointerType !== 'mouse') return
  // Softer than a phone: the pointer is always somewhere, and a table that
  // leans hard towards it wherever it rests would never be still.
  follow(clamp(((e.clientX / window.innerWidth) * 2 - 1) * 0.6), clamp(((e.clientY / window.innerHeight) * 2 - 1) * 0.6))
}

/** Take a reading as the new target — unless it is only the hand's tremor
 *  (`DEAD_ZONE`), in which case nothing changes and nothing is drawn. */
function follow(x: number, y: number): void {
  if (Math.abs(x - target.x) < DEAD_ZONE && Math.abs(y - target.y) < DEAD_ZONE) return
  target = { x, y }
  kick()
}

function kick(): void {
  if (!frame) frame = requestAnimationFrame(step)
}

function step(now: number): void {
  frame = 0
  if (!running) return
  // Every other frame on a 60 Hz screen, every fourth on a 120 Hz one.
  if (now - lastStep < FRAME_MS - 4) {
    kick()
    return
  }
  lastStep = now
  shown = { x: shown.x + (target.x - shown.x) * FOLLOW, y: shown.y + (target.y - shown.y) * FOLLOW }
  if (!(Math.abs(shown.x - written.x) < EPSILON && Math.abs(shown.y - written.y) < EPSILON)) {
    for (const el of targets) write(el, shown)
    written = { ...shown }
  }
  // Keep going until the table has caught up with the phone, then rest: an
  // idle table costs no frames at all.
  if (Math.abs(target.x - shown.x) > EPSILON || Math.abs(target.y - shown.y) > EPSILON) kick()
}

function clamp(v: number): number {
  return Math.max(-1, Math.min(1, v))
}
